import React, { useEffect, useState } from 'react';
import supabase from '../config/supabase';
import { invalidatePLCategoryCache } from '../utils/plCategoryResolver';
import PNLSetupWizard from './PNLSetupWizard';
import PNLCategoryMappingPanel from './PNLCategoryMapping';

const DARK_BROWN = '#3E2723';
const LIGHT_BORDER = '#E8DDD0';
const MUTED = '#8B7B73';

// onDone (optional): handler invoked when the user saves/cancels. When omitted,
// it navigates back to the dashboard so users who land here directly have a
// clear way out.
const PNLCategoriesSettings = ({ onDone } = {}) => {
  const handleDone = () => {
    if (typeof onDone === 'function') return onDone();
    window.location.href = '/dashboard.html';
  };
  const [storeId, setStoreId] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pnlCats, setPnlCats] = useState([]);
  const [editing, setEditing] = useState(null);   // { id, name }
  // Bumping this remounts the assignment panel so it reloads after a P&L
  // category is renamed, deleted, or the guided wizard finishes.
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    setError('');
    const sid = Number(localStorage.getItem('selectedStoreId'));
    if (!sid) { setError('No store selected.'); setLoading(false); return; }
    setStoreId(sid);

    const pnlRes = await supabase
      .from('pnl_categories')
      .select('id,name,sort_order')
      .eq('store_id', sid)
      .order('sort_order', { ascending: true });

    if (pnlRes.error) { setError(pnlRes.error.message); setLoading(false); return; }
    setPnlCats(pnlRes.data || []);
    setLoading(false);
  };

  const refreshCats = async () => {
    const { data } = await supabase
      .from('pnl_categories')
      .select('id,name,sort_order')
      .eq('store_id', storeId)
      .order('sort_order', { ascending: true });
    setPnlCats(data || []);
  };

  const saveEdit = async (id, name) => {
    const t = (name || '').trim();
    if (!t) { setEditing(null); return; }
    const { error: err } = await supabase.from('pnl_categories').update({ name: t }).eq('id', id);
    if (err) { setError(err.message); return; }
    setEditing(null);
    invalidatePLCategoryCache(storeId);
    await refreshCats();
    setReloadNonce((n) => n + 1);
  };

  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Inventory categories assigned to it will become unassigned.`)) return;
    // pnl_category_mappings rows cascade-delete via the FK on pnl_category_id.
    await supabase.from('pnl_categories').delete().eq('id', id);
    invalidatePLCategoryCache(storeId);
    await refreshCats();
    setReloadNonce((n) => n + 1);
  };

  if (loading) return <div style={{ padding: '1rem', color: MUTED }}>Loading…</div>;

  return (
    <div style={{ fontFamily: "'Montserrat',sans-serif" }}>
      {error && <div style={{ color: '#dc3545', marginBottom: '0.75rem', fontSize: '0.85rem' }}>{error}</div>}

      {showWizard && (
        <PNLSetupWizard
          storeId={storeId}
          isRerun
          onClose={() => setShowWizard(false)}
          onComplete={() => { setShowWizard(false); init(); setReloadNonce((n) => n + 1); }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button onClick={() => setShowWizard(true)}
          style={{ padding: '6px 14px', borderRadius: 6, background: '#FAF6F2', color: '#5A4A42', border: '1px solid #D4C4B0', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
          🧭 Guided setup
        </button>
      </div>

      {storeId && (
        <PNLCategoryMappingPanel
          key={reloadNonce}
          storeId={storeId}
          includeClover={false}
          onSave={handleDone}
          onCancel={handleDone}
          saveLabel="Save Category Assignments"
        />
      )}

      {/* De-emphasized P&L category management (rename / delete). Adding new
          categories happens inline from the assignment table above. */}
      {pnlCats.length > 0 && (
        <details style={{ marginTop: '1rem', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 10, background: '#FFFDF9' }}>
          <summary style={{ cursor: 'pointer', padding: '0.6rem 0.8rem', fontSize: '0.82rem', fontWeight: 700, color: DARK_BROWN }}>
            Manage P&amp;L categories ({pnlCats.length})
          </summary>
          <div style={{ padding: '0 0.8rem 0.75rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pnlCats.map((cat) => (
              <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: `1px solid ${LIGHT_BORDER}` }}>
                {editing?.id === cat.id ? (
                  <input autoFocus value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(cat.id, editing.name); if (e.key === 'Escape') setEditing(null); }}
                    onBlur={() => saveEdit(cat.id, editing.name)}
                    aria-label={`Rename ${cat.name}`}
                    style={{ flex: 1, padding: '4px 8px', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 6, fontSize: '0.85rem', outline: 'none' }} />
                ) : (
                  <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: DARK_BROWN }}>{cat.name}</span>
                )}
                <button onClick={() => setEditing({ id: cat.id, name: cat.name })}
                  style={{ background: 'none', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem', padding: '3px 8px', color: '#5A4A42' }}>
                  Rename
                </button>
                <button onClick={() => deleteCategory(cat.id, cat.name)}
                  style={{ background: 'none', border: `1px solid ${LIGHT_BORDER}`, borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem', padding: '3px 8px', color: '#a04040' }}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default PNLCategoriesSettings;
